import { useState } from "react";
import { Archive, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const hasStudentWork = (assignment) => Boolean(
  ["submitted", "graded"].includes(assignment?.status)
  || assignment?.grade != null
  || assignment?.submission_content?.trim()
  || assignment?.submission_reflection?.trim()
  || assignment?.submission_link?.trim()
  || assignment?.submission_attachments?.length
  || assignment?.submitted_at
);

export const AssignmentDeleteDialog = ({ assignment, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const mustArchive = hasStudentWork(assignment);

  const confirm = async () => {
    setLoading(true);
    try {
      if (mustArchive) {
        await api.post(`/assignments/${assignment.id}/archive`);
        toast.success("Assignment archived; student work and grade were preserved");
      } else {
        await api.delete(`/assignments/${assignment.id}`);
        toast.success("Assignment permanently deleted");
      }
      await onChanged();
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || detail || "Could not update assignment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={Boolean(assignment)} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent data-testid="assignment-delete-dialog" className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {mustArchive ? `Archive “${assignment?.title}”?` : `Delete “${assignment?.title}” permanently?`}
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="assignment-delete-warning">
            {mustArchive
              ? "This assignment has submitted student work or a grade. Permanent deletion is blocked. Archive it instead so the work and grade remain available in the student report."
              : "This permanently removes this assignment and its attached files. This action cannot be undone."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="assignment-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="assignment-delete-confirm" onClick={confirm} disabled={loading}
            className={mustArchive ? "bg-amber-600 hover:bg-amber-700" : "bg-red-700 hover:bg-red-800"}>
            {mustArchive ? <Archive size={15} className="mr-2" /> : <Trash2 size={15} className="mr-2" />}
            {loading ? "Working…" : mustArchive ? "Archive assignment" : "Delete permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};